/**
 * The content layer.
 *
 * Everything the site renders comes from `src/content/*.json`, which was
 * extracted from the WordPress database and verified character-for-character.
 * Those files are read-only. Nothing here rewrites, shortens or reformats a
 * content string; the helpers only look content up and derive route lists.
 *
 * The page content is split for delivery. `scripts/split-content.mjs` turns
 * `src/content/pages.json` into
 *   `src/generated/index.json`       metadata for all 38 pages, loaded eagerly
 *   `src/generated/blocks/<id>.json` one file per page, loaded per route
 * so a visitor downloads the copy of the page they asked for and nothing else.
 * The split runs before every build, dev server and typecheck, so pages.json
 * remains the single source of truth.
 *
 * Everything a listing, an archive, the navigation, the breadcrumbs, the
 * sitemap or the document head needs is in the index: `teaser` and `thumbnail`
 * are precomputed there precisely so none of them ever pulls a block file.
 */
import { use } from 'react';
import indexData from '@/generated/index.json';
import chromeData from '@/content/chrome.json';
import chartsData from '@/content/charts.json';
import imagesData from '@/content/images.json';
import archivesData from '@/content/archives.json';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type PageType = 'page' | 'post' | 'liquid-portfolio';
export type PageStatus = 'publish' | 'private' | 'draft';

export type Term = {
  taxonomy: string;
  name: string;
  slug: string;
};

export type Seo = {
  title: string | null;
  description: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: string | null;
  robots: string | null;
};

export type ImageRef = {
  id?: string;
  file?: string;
  alt: string;
  title?: string;
  src: string;
};

export type HtmlBlockData = { type: 'html'; html: string };
export type HeadingBlockData = {
  type: 'heading';
  tag: string;
  html: string;
  text: string;
  fs?: string | null;
  align?: string | null;
};
export type SectionTitleBlockData = {
  type: 'section_title';
  html: string;
  text: string;
  subtitle: string;
  raw_subtitle?: string | null;
};
export type ImageBlockData = {
  type: 'image';
  image: ImageRef;
  size?: string | null;
  action?: { text: string; url: string };
};
export type ButtonBlockData = {
  type: 'button';
  text: string;
  url: string | null;
  style?: string | null;
};
export type AccordionItemBlockData = { type: 'accordion_item'; title: string; body: Block[] };
export type CounterBlockData = { type: 'counter'; value: string; label: string };
export type SeparatorBlockData = { type: 'separator' };
export type PortfolioListingBlockData = {
  type: 'portfolio_listing';
  attrs?: Record<string, string>;
};
export type BlogListingBlockData = { type: 'blog_listing'; attrs?: Record<string, string> };
export type ChartBlockData = { type: 'chart'; id: string };
export type FormBlockData = { type: 'form'; id: string };
export type InstagramBlockData = { type: 'instagram'; attrs?: Record<string, string> };

export type Block =
  | HtmlBlockData
  | HeadingBlockData
  | SectionTitleBlockData
  | ImageBlockData
  | ButtonBlockData
  | AccordionItemBlockData
  | CounterBlockData
  | SeparatorBlockData
  | PortfolioListingBlockData
  | BlogListingBlockData
  | ChartBlockData
  | FormBlockData
  | InstagramBlockData;

/**
 * A page as the eager index knows it: everything except the copy itself.
 * The blocks live in their own chunk; `usePageBlocks` fetches them.
 */
export type PageMeta = {
  id: string;
  type: PageType;
  status: PageStatus;
  slug: string;
  route: string;
  title: string;
  excerpt: string;
  date: string;
  modified: string;
  menu_order: string;
  terms: Term[];
  seo: Seo;
  /**
   * The page's plain-text lead, at most `TEASER_CHARS` long, derived by
   * `scripts/split-content.mjs` with exactly the walk `leadTextOf` used to do
   * over the blocks. Read it through `leadTextOf`, never directly.
   */
  teaser: string;
  /** The page's first image, block or inline, or null. */
  thumbnail: ImageRef | null;
};

export type ImageRendition = { w: number; url: string };
export type ImageAsset = {
  src: string;
  width: number;
  height: number;
  sources: ImageRendition[];
};

export type ChartDataset = {
  id: string;
  type: string;
  columns: string[];
  rows: (string | number | null)[][];
};

export type NavItem = { label: string; href: string };

export type Chrome = typeof chromeData;

/* -------------------------------------------------------------------------- */
/* Loaders                                                                    */
/* -------------------------------------------------------------------------- */

export const pages: PageMeta[] = (indexData as unknown as { pages: PageMeta[] }).pages;
export const chrome = chromeData as Chrome;
// charts.json is keyed by chart id; keep a stable ordered list as well.
const chartsSource = chartsData as unknown as
  | Record<string, ChartDataset>
  | ChartDataset[];
export const charts: ChartDataset[] = Array.isArray(chartsSource)
  ? chartsSource
  : Object.values(chartsSource);
export const images: Record<string, ImageAsset> = imagesData as unknown as Record<
  string,
  ImageAsset
>;
export const archiveRoutes: string[] = (archivesData as unknown as { archives: string[] })
  .archives;

export const SITE_ORIGIN = 'https://health-prime.ch';

/** Hostnames that are really this site, so their absolute links stay internal. */
export const INTERNAL_HOSTS = new Set(['health-prime.ch', 'www.health-prime.ch']);

/* -------------------------------------------------------------------------- */
/* Blocks                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * One lazy loader per page, resolved by Vite at build time into one chunk per
 * page. The glob pattern has to be a literal: a variable in the import path
 * would leave Rollup nothing to analyse and emit nothing.
 */
const blockModules = import.meta.glob<Block[]>('../generated/blocks/*.json', {
  import: 'default',
});

const blockCache = new Map<string, Promise<Block[]>>();

/**
 * The blocks of one page, as a promise that is stable across renders so it can
 * be handed to `use()`. A failed load drops out of the cache so that the next
 * render retries instead of repeating the failure forever.
 */
export function loadBlocks(id: string): Promise<Block[]> {
  const cached = blockCache.get(id);
  if (cached) return cached;
  const loader = blockModules[`../generated/blocks/${id}.json`];
  const promise = loader
    ? loader().catch((error: unknown) => {
        blockCache.delete(id);
        throw error;
      })
    : Promise.resolve<Block[]>([]);
  blockCache.set(id, promise);
  return promise;
}

/**
 * Start a page's block fetch without waiting for it. Used for the route the
 * visitor landed on and for links they are about to follow, so that navigating
 * does not have to wait for a round trip.
 */
export function preloadBlocks(page: PageMeta): void {
  loadBlocks(page.id).catch(() => {
    // A speculative fetch that fails is not an error. The render that really
    // needs the blocks will ask again and report the failure then.
  });
}

/** Blocks for a page, suspending the component until its chunk has arrived. */
export function usePageBlocks(page: PageMeta): Block[] {
  return use(loadBlocks(page.id));
}

/* -------------------------------------------------------------------------- */
/* Routes                                                                     */
/* -------------------------------------------------------------------------- */

/** Canonicalise a path: no trailing slash, always leading slash. */
export function normalizeRoute(pathname: string): string {
  if (!pathname) return '/';
  let p = pathname;
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return p || '/';
}

/** Content that is allowed into the router. Drafts never are. */
export const routablePages: PageMeta[] = pages.filter(
  (p) => p.status === 'publish' || p.status === 'private',
);

/** Content that search engines and the sitemap may see. */
export const publishedPages: PageMeta[] = pages.filter((p) => p.status === 'publish');

const byRoute = new Map<string, PageMeta>();
for (const p of routablePages) byRoute.set(p.route, p);

export function getPage(route: string): PageMeta | undefined {
  return byRoute.get(normalizeRoute(route));
}

/** Every page-ish route the SPA answers, derived from the data. */
export const contentRoutes: string[] = routablePages.map((p) => p.route);

export function pagesOfType(type: PageType): PageMeta[] {
  return routablePages.filter((p) => p.type === type);
}

/** Posts, newest first. */
export const posts: PageMeta[] = publishedPages
  .filter((p) => p.type === 'post')
  .slice()
  .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

/** Published screening items, in title order. */
export const portfolioItems: PageMeta[] = publishedPages
  .filter((p) => p.type === 'liquid-portfolio')
  .slice()
  .sort((a, b) => a.title.localeCompare(b.title, 'de-CH'));

/** Start the landing route's block fetch in parallel with React booting. */
export function preloadRoute(route: string): void {
  const page = getPage(route);
  if (page) preloadBlocks(page);
}

if (typeof window !== 'undefined') preloadRoute(window.location.pathname);

/* -------------------------------------------------------------------------- */
/* Taxonomy                                                                   */
/* -------------------------------------------------------------------------- */

/** All terms of one taxonomy that actually occur on published content. */
export function termsOf(taxonomy: string): Term[] {
  const found = new Map<string, Term>();
  for (const p of publishedPages) {
    for (const t of p.terms) {
      if (t.taxonomy === taxonomy && !found.has(t.slug)) found.set(t.slug, t);
    }
  }
  return [...found.values()].sort((a, b) => a.name.localeCompare(b.name, 'de-CH'));
}

export function pagesWithTerm(taxonomy: string, slug: string): PageMeta[] {
  return publishedPages.filter((p) =>
    p.terms.some((t) => t.taxonomy === taxonomy && t.slug === slug),
  );
}

export function findTerm(taxonomy: string, slug: string): Term | undefined {
  for (const p of publishedPages) {
    for (const t of p.terms) {
      if (t.taxonomy === taxonomy && t.slug === slug) return t;
    }
  }
  return undefined;
}

export function termsOfPage(page: PageMeta, taxonomy: string): Term[] {
  return page.terms.filter((t) => t.taxonomy === taxonomy);
}

/* -------------------------------------------------------------------------- */
/* Media                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * WordPress upload paths still appear inside `html` blocks and inside the
 * recovered SEO metadata. The files themselves were moved to /media, so the
 * path (never the copy) is remapped at render time.
 */
export function toMediaPath(src: string): string {
  if (!src) return src;
  let out = src;
  const abs = /^https?:\/\/([^/]+)(\/.*)$/i.exec(out);
  if (abs && INTERNAL_HOSTS.has(abs[1].toLowerCase())) out = abs[2];
  out = out.replace(/^\/wp-content\/uploads\//, '/media/');
  return out;
}

export function getImage(src: string): ImageAsset | undefined {
  return images[toMediaPath(src)];
}

export function srcSetOf(asset: ImageAsset): string {
  return asset.sources.map((s) => `${s.url} ${s.w}w`).join(', ');
}

/** The widest rendition, used for Open Graph cards. */
export function largestRendition(asset: ImageAsset): string {
  return asset.sources.reduce(
    (best, s) => (s.w > best.w ? s : best),
    asset.sources[0] ?? { w: 0, url: asset.src },
  ).url;
}

/**
 * First usable image on a page: an image block, else the first inline <img>.
 * Precomputed into the index, so cards and Open Graph tags cost no block fetch.
 */
export function firstImageOf(page: PageMeta): string | null {
  return page.thumbnail?.src ?? null;
}

/* -------------------------------------------------------------------------- */
/* Charts                                                                     */
/* -------------------------------------------------------------------------- */

const chartById = new Map<string, ChartDataset>();
for (const c of charts) chartById.set(c.id, c);

export function getChart(id: string): ChartDataset | undefined {
  return chartById.get(id);
}

/* -------------------------------------------------------------------------- */
/* Text helpers                                                               */
/* -------------------------------------------------------------------------- */

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  szlig: 'ß',
  auml: 'ä',
  ouml: 'ö',
  uuml: 'ü',
  Auml: 'Ä',
  Ouml: 'Ö',
  Uuml: 'Ü',
  ndash: '–',
  mdash: '—',
  hellip: '…',
  laquo: '«',
  raquo: '»',
  rsquo: '’',
  lsquo: '‘',
  ldquo: '“',
  rdquo: '”',
  eacute: 'é',
  egrave: 'è',
  agrave: 'à',
  deg: '°',
  euro: '€',
};

/**
 * Strip tags and decode the entities WordPress leaves behind. Read-only use.
 * `scripts/split-content.mjs` carries a copy of this and of ENTITIES so the
 * teasers it precomputes are the ones this module would have produced.
 */
export function plainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n: string) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&([a-zA-Z]+);/g, (all, name: string) => ENTITIES[name] ?? all)
    .replace(/\s+/g, ' ')
    .trim();
}

/** How much plain text `teaser` holds. Mirrors TEASER_CHARS in the splitter. */
const TEASER_CHARS = 240;

/**
 * Plain-text lead for teasers and meta descriptions. Never shown as body copy.
 *
 * Reads the precomputed teaser, so a listing or a document head costs no block
 * fetch. `maxChars` must stay below TEASER_CHARS; past that the teaser has
 * already been cut and the result would silently lose the ellipsis.
 */
export function leadTextOf(page: PageMeta, maxChars = 200): string {
  if (page.excerpt && page.excerpt.trim()) return page.excerpt.trim();
  const text = page.teaser ?? '';
  const limit = Math.min(maxChars, TEASER_CHARS - 1);
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 60 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
}

/* -------------------------------------------------------------------------- */
/* Formatting                                                                 */
/* -------------------------------------------------------------------------- */

const numberFormat = new Intl.NumberFormat('de-CH');

export function formatNumber(value: number): string {
  return numberFormat.format(value);
}

const dateFormat = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
});

/** `2025-01-17 06:21:25` -> a de-CH long date. */
export function formatDate(sqlDate: string): string {
  const d = parseDate(sqlDate);
  return d ? dateFormat.format(d) : '';
}

export function parseDate(sqlDate: string): Date | null {
  if (!sqlDate) return null;
  const d = new Date(sqlDate.replace(' ', 'T') + 'Z');
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO 8601 date for <time datetime> and JSON-LD. */
export function isoDate(sqlDate: string): string {
  const d = parseDate(sqlDate);
  return d ? d.toISOString() : '';
}
